import { createContext, useMemo, useContext, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';
import { useFormik } from 'formik';

import { checkRol } from '../security';
import { proveedorNotificador } from '../security/constantsRols';
import { getFiltrosPTSDS, postFiltrosPTSDS } from '../services/serviceRequest';
import useToast from '../hooks/useToast';
import useAsync from '../hooks/useAsync';
import {
  postFilterPreferenceDetailSDSAdapter,
  postFilterPreferencesDetailMSDSAdapter
} from '../adapters/filters/postFiltersPreferences';
import {
  filterMSDSRequestAdapter,
  filterRequestAdapter
} from '../adapters/filters/getFiltersPreferences';
import { getFilterPTMSDSDetail, postFilterPTMSDSDetail } from '../services/monthlyServiceRequest';

const PositionLineFilterContext = createContext();

export const useFilterContext = () => useContext(PositionLineFilterContext);

const DetailsFiltersProvider = ({ children }) => {
  const { setNewToast } = useToast();
  const location = useLocation();

  const isProviderSDS = checkRol(proveedorNotificador) && location.pathname.includes('/detail/');
  const idRequest = location.pathname.includes('simplifiedcharging')
    ? location.pathname.split('/').slice(2)[0]
    : location.pathname.split('/').slice(-1)[0];

  const initialFiltersPrefrencesValues = useMemo(
    () => ({
      events: [],
      ubicationType: null,
      ubications: null,
      towerEquipments: [],
      position: null,
      line: null,
      posicionesLineas: [],
      haveFilters: false
    }),
    []
  );

  const getFiltersPreferencesFromDB = useCallback(async () => {
    try {
      let res;

      if (isProviderSDS) {
        res = filterRequestAdapter(await getFiltrosPTSDS(idRequest));
      } else {
        res = filterMSDSRequestAdapter(await getFilterPTMSDSDetail(idRequest));
      }
      if (res?.length === 0 || (typeof res === 'string' && res.trim() === '')) {
        return initialFiltersPrefrencesValues;
      }

      return res;
    } catch (error) {
      setNewToast({
        type: 'ERROR',
        message: error.message
      });

      return initialFiltersPrefrencesValues;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFiltersPrefrencesValues, setNewToast, idRequest]);

  const initializeFiltersPreferences = useCallback(async () => {
    const filtersFromDB = await getFiltersPreferencesFromDB();

    return filtersFromDB;
  }, [getFiltersPreferencesFromDB]);

  const { data: filtersPreferences } = useAsync(initializeFiltersPreferences);

  const [filtersPreferencesState, setFiltersPreferencesState] = useState(
    initialFiltersPrefrencesValues
  );

  const formikFilters = useFormik({
    initialValues: filtersPreferences ?? initialFiltersPrefrencesValues,
    onSubmit: (values) => {
      setFiltersPreferencesState(values);
    },
    enableReinitialize: true
  });

  const updateFiltersPreferences = useCallback(
    async (filters) => {
      try {
        if (isProviderSDS) {
          postFiltrosPTSDS(idRequest, postFilterPreferenceDetailSDSAdapter(filters));
        } else {
          postFilterPTMSDSDetail(idRequest, postFilterPreferencesDetailMSDSAdapter(filters));
        }
        setNewToast({
          type: 'SUCCESS',
          message: 'Filtros guardados con éxito.'
        });
      } catch (err) {
        setNewToast({
          type: 'ERROR',
          message: err.message
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setNewToast, isProviderSDS]
  );

  const handleSaveFilters = useCallback(() => {
    updateFiltersPreferences(formikFilters.values);
  }, [formikFilters.values, updateFiltersPreferences]);

  const handleClearFilters = useCallback(() => {
    setFiltersPreferencesState(initialFiltersPrefrencesValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikFilters, initialFiltersPrefrencesValues]);

  const handleChangeFilter = useCallback(
    (newFilters) => setFiltersPreferencesState(newFilters),
    []
  );

  const handleChip = useCallback(
    (chip) => {
      const newChips = [...formikFilters.values.posicionesLineas, chip];

      formikFilters.setFieldValue('posicionesLineas', newChips);
    },
    [formikFilters]
  );

  const handleDeleteChip = useCallback(
    (chipIndex) => {
      const newChips = formikFilters.values.posicionesLineas.filter(
        (chip, index) => index !== chipIndex
      );

      formikFilters.setFieldValue('posicionesLineas', newChips);
    },
    [formikFilters]
  );

  useEffect(() => {
    if (filtersPreferences) {
      handleChangeFilter(filtersPreferences);
    }
  }, [filtersPreferences, handleChangeFilter]);

  return (
    <PositionLineFilterContext.Provider
      value={{
        filter: filtersPreferencesState,
        formikFilters,
        handleSaveFilters,
        handleClearFilters,
        handleChip,
        handleDeleteChip
      }}
    >
      {children}
    </PositionLineFilterContext.Provider>
  );
};

DetailsFiltersProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default DetailsFiltersProvider;
